import { produce } from 'immer';
import type { StoreApi } from 'zustand';

// Floor chosen so the sidebar tab strip (Searches · Sites · Layers · Legend
// + collapse chevron) always fits without clipping. The tabs render at their
// natural content width (no horizontal scroll) and the collapse chevron is
// flex-shrink-0, so if the panel is narrower than the full strip the chevron
// gets pushed off-screen and the panel becomes uncloseable (GEO-862). The
// previous 420 floor was a few px too narrow once all four tabs are present.
export const MIN_LAYERS_PANEL_WIDTH = 480;
// Default must be ≥ the floor so a fresh/invalid saved width never loads below
// it (loadPanelWidth validates saved values against the floor and falls back
// to this default).
export const DEFAULT_LAYERS_PANEL_WIDTH = 480;
// Raised ~20% from the original 600 (GEO-862 follow-up) so the panel can grow
// wider — useful now that the Sites/Searches tabs pack more horizontal content.
export const MAX_LAYERS_PANEL_WIDTH = 720;
const LAYERS_PANEL_WIDTH_STORAGE_KEY = 'geoforge-layers-panel-width';

// Load saved panel width from localStorage
const loadPanelWidth = (): number => {
  if (typeof window === 'undefined') {
    return DEFAULT_LAYERS_PANEL_WIDTH;
  }
  try {
    const saved = localStorage.getItem(LAYERS_PANEL_WIDTH_STORAGE_KEY);
    if (saved) {
      const width = parseInt(saved, 10);
      if (!isNaN(width) && width >= MIN_LAYERS_PANEL_WIDTH && width <= MAX_LAYERS_PANEL_WIDTH) {
        return width;
      }
    }
  } catch (error) {
    // localStorage might not be available
    console.warn('Failed to load panel width from localStorage:', error);
  }
  return DEFAULT_LAYERS_PANEL_WIDTH;
};

// Save panel width to localStorage
const savePanelWidth = (width: number): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(LAYERS_PANEL_WIDTH_STORAGE_KEY, width.toString());
  } catch (error) {
    // localStorage might not be available
    console.warn('Failed to save panel width to localStorage:', error);
  }
};

export type AgentPanelMode = 'slide' | 'full';

export type MapPanelsUIState = {
  layerStylingPanelOpen: boolean;
  currentStylingLayerId: string | null;
  layersPanelExpanded: boolean;
  layersPanelWidth: number;
  inboundTrackerExpanded: boolean;
  inboundTrackerLoading: boolean;
  inboundTrackerVersion: number;
  agentPanelOpen: boolean;
  currentAgentLayerId: string | null;
  agentPanelMode: AgentPanelMode;
  agentPanelPendingPrompt: string | null;
  sidebarSearchActiveId: string | null;
  // Layer id of a sidebar search result rendered as a transient map preview.
  // It is intentionally NOT a map_layers row, so it shows on the map while a
  // search is open but never appears in the Layers tab.
  searchPreviewLayerId: string | null;
  openLayerStylingPanel: (layerId: string) => void;
  closeLayerStylingPanel: () => void;
  openLayersPanel: () => void;
  toggleLayersPanelExpanded: () => void;
  setLayersPanelWidth: (width: number) => void;
  toggleInboundTrackerExpanded: () => void;
  setInboundTrackerExpanded: (expanded: boolean) => void;
  setInboundTrackerLoading: (loading: boolean) => void;
  invalidateInboundTracker: () => void;
  openAgentPanel: (layerId: string, options?: { pendingPrompt?: string }) => void;
  closeAgentPanel: () => void;
  setAgentPanelMode: (mode: AgentPanelMode) => void;
  consumeAgentPanelPendingPrompt: () => void;
  openSidebarSearch: (searchId: string) => void;
  closeSidebarSearch: () => void;
  setSearchPreviewLayerId: (layerId: string | null) => void;
  clearMapPanelsUI: () => void;
};

const initialMapPanelsUIState = {
  layerStylingPanelOpen: false,
  currentStylingLayerId: null,
  layersPanelExpanded: true,
  layersPanelWidth: loadPanelWidth(),
  inboundTrackerExpanded: false,
  inboundTrackerLoading: true,
  inboundTrackerVersion: 0,
  agentPanelOpen: false,
  currentAgentLayerId: null,
  agentPanelMode: 'slide' as AgentPanelMode,
  agentPanelPendingPrompt: null,
  sidebarSearchActiveId: null,
  searchPreviewLayerId: null,
};

export const createMapPanelsUISlice = (set: StoreApi<any>['setState']) => ({
  ...initialMapPanelsUIState,
  openLayerStylingPanel: (layerId: string) =>
    set(
      produce((draft: MapPanelsUIState) => {
        draft.layerStylingPanelOpen = true;
        draft.currentStylingLayerId = layerId;
      })
    ),
  closeLayerStylingPanel: () =>
    set(
      produce((draft: MapPanelsUIState) => {
        draft.layerStylingPanelOpen = false;
        draft.currentStylingLayerId = null;
      })
    ),
  openLayersPanel: () =>
    set(
      produce((draft: MapPanelsUIState) => {
        draft.layersPanelExpanded = true;
        // When layers panel expands, collapse the inbound tracker
        draft.inboundTrackerExpanded = false;
      })
    ),
  toggleLayersPanelExpanded: () =>
    set(
      produce((draft: MapPanelsUIState) => {
        draft.layersPanelExpanded = !draft.layersPanelExpanded;
        // When layers panel expands, collapse the inbound tracker
        if (draft.layersPanelExpanded) {
          draft.inboundTrackerExpanded = false;
        }
      })
    ),
  setLayersPanelWidth: (width: number) =>
    set(
      produce((draft: MapPanelsUIState) => {
        const clampedWidth = Math.min(
          MAX_LAYERS_PANEL_WIDTH,
          Math.max(MIN_LAYERS_PANEL_WIDTH, width)
        );
        draft.layersPanelWidth = clampedWidth;
        savePanelWidth(clampedWidth);
      })
    ),
  toggleInboundTrackerExpanded: () =>
    set(
      produce((draft: MapPanelsUIState) => {
        draft.inboundTrackerExpanded = !draft.inboundTrackerExpanded;
        // When inbound tracker expands, collapse the layers panel
        if (draft.inboundTrackerExpanded) {
          draft.layersPanelExpanded = false;
        }
      })
    ),
  setInboundTrackerExpanded: (expanded: boolean) =>
    set(
      produce((draft: MapPanelsUIState) => {
        draft.inboundTrackerExpanded = expanded;
        // When inbound tracker expands, collapse the layers panel
        if (expanded) {
          draft.layersPanelExpanded = false;
        }
      })
    ),
  setInboundTrackerLoading: (loading: boolean) =>
    set(
      produce((draft: MapPanelsUIState) => {
        draft.inboundTrackerLoading = loading;
      })
    ),
  invalidateInboundTracker: () =>
    set(
      produce((draft: MapPanelsUIState) => {
        draft.inboundTrackerVersion += 1;
      })
    ),
  openAgentPanel: (layerId: string, options?: { pendingPrompt?: string }) =>
    set(
      produce((draft: MapPanelsUIState) => {
        draft.agentPanelOpen = true;
        draft.currentAgentLayerId = layerId;
        draft.agentPanelPendingPrompt = options?.pendingPrompt ?? null;
        draft.layersPanelExpanded = true;
        draft.layerStylingPanelOpen = false;
        draft.currentStylingLayerId = null;
        draft.sidebarSearchActiveId = null;
      })
    ),
  closeAgentPanel: () =>
    set(
      produce((draft: MapPanelsUIState) => {
        draft.agentPanelOpen = false;
        // Intentionally keep `currentAgentLayerId` set so the underlying
        // <LayerAgentPanel> stays mounted and its useAgentStream subscription
        // survives the close→reopen cycle (test: streaming resumes in place).
      })
    ),
  setAgentPanelMode: (mode: AgentPanelMode) =>
    set(
      produce((draft: MapPanelsUIState) => {
        draft.agentPanelMode = mode;
      })
    ),
  consumeAgentPanelPendingPrompt: () =>
    set(
      produce((draft: MapPanelsUIState) => {
        draft.agentPanelPendingPrompt = null;
      })
    ),
  openSidebarSearch: (searchId: string) =>
    set(
      produce((draft: MapPanelsUIState) => {
        draft.sidebarSearchActiveId = searchId;
        draft.agentPanelOpen = false;
      })
    ),
  closeSidebarSearch: () =>
    set(
      produce((draft: MapPanelsUIState) => {
        draft.sidebarSearchActiveId = null;
      })
    ),
  setSearchPreviewLayerId: (layerId: string | null) =>
    set(
      produce((draft: MapPanelsUIState) => {
        draft.searchPreviewLayerId = layerId;
      })
    ),
  clearMapPanelsUI: () => set(() => initialMapPanelsUIState),
});
